import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { onAuthChange, getUserPermissions } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import * as Sentry from '@sentry/react';

function setSentryUser(user: User | null) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  if (user) {
    Sentry.setUser({ id: user.uid, email: user.email ?? undefined });
  } else {
    Sentry.setUser(null);
  }
}

interface AuthContextType {
  user: User | null;
  userProfile: any | null;
  userPermissions: string[];
  loading: boolean;
  signOut: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  canAccessPage: (page: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<any | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      setUser(user);
      setSentryUser(user);
      
      if (user) {
        // Get user profile and permissions
        setUserProfile({ uid: user.uid, email: user.email });
        
        try {
          const permissions = await getUserPermissions(user.uid);
          setUserPermissions(permissions);
        } catch (error) {
          logger.error('Error fetching user permissions:', error);
          setUserPermissions([]);
        }
      } else {
        setUserProfile(null);
        setUserPermissions([]);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signOut = async () => {
    const { signOutUser } = await import('@/lib/firebase');
    await signOutUser();
  };

  const hasPermission = (permission: string): boolean => {
    return userPermissions.includes('administrator') || userPermissions.includes(permission);
  };

  const canAccessPage = (page: string): boolean => {
    // Administrator has access to all pages
    if (userPermissions.includes('administrator')) {
      return true;
    }

    // Sales can only access their Dashboard, Customers / Partners, and Trials
    if (userPermissions.includes('sales')) {
      return page === 'sales-dashboard' || page === 'customers' || page === 'trials';
    }

    // Check specific page permissions
    switch (page) {
      case 'videos':
      case 'series':
        return userPermissions.includes('videoManager');
      case 'articles':
        return userPermissions.includes('articleManager');
      case 'user-management':
        return userPermissions.includes('administrator'); // Only admin can access user management
      case 'categories':
      case 'authors':
      case 'promotions':
      case 'events':
      case 'programs':
      case 'badges':
      case 'milestones':
      case 'newsletters':
      case 'partnerships':
      case 'customers':
      case 'trials':
      case 'company-audit':
      case 'settings':
      case 'pulse-survey-categories':
      case 'pulse-survey-questions':
      case 'pulse-survey-templates':
        return userPermissions.includes('administrator'); // Only administrator can access these pages
      case 'sales-dashboard':
        return false; // Only administrator (handled above) and sales (handled above) can access
      default:
        return true; // Default pages like dashboard are accessible to all authenticated users
    }
  };

  const value = {
    user,
    userProfile,
    userPermissions,
    loading,
    signOut,
    hasPermission,
    canAccessPage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
